import { useId } from 'react';
import type { GameSave } from '@/app/services';
import { useUiImage } from '@/ui/art/uiArt';
import styles from './AvatarImage.module.css';

type AvatarState = GameSave['avatar'];

const INK = '#5a4236';
const SKIN = '#ffe2c8';
const HAIR = '#8a6248';
const HAIR_DARK = '#6f4c37';

/**
 * Art layers of one look (asset-list `avt_*`, art-src/avatar): the bust is drawn per hair × hat so every hat sits
 * on its hair, `avt_shirt_*` is the mask of its light grey t-shirt for the runtime tint, `afr_*` rings on top.
 */
export function avatarLayers(avatar: AvatarState): { bust: string; shirt: string } {
  const look = `${avatar.hair.slice('av_hair_'.length)}_${avatar.hat.slice('av_hat_'.length)}`;
  return { bust: `avt_bust_${look}`, shirt: `avt_shirt_${look}` };
}

/**
 * The player's chibi bust, stacked from 512² PNG layers on one canvas (crisp up to ~170 px at 3× DPR). Falls back
 * to the vector placeholder while a look has no art yet.
 */
export function AvatarImage({
  avatar,
  tint,
  size,
}: {
  avatar: AvatarState;
  /** Shirt colour from the part's `tint` (one shared shirt sprite, style-guide §2). */
  tint: string;
  size: number;
}) {
  const layers = avatarLayers(avatar);
  const bust = useUiImage(layers.bust);
  const shirt = useUiImage(layers.shirt);
  const frame = useUiImage(avatar.frame);
  if (!bust) return <AvatarSvg avatar={avatar} tint={tint} size={size} />;
  const mask = shirt ? `url("${shirt}")` : undefined;
  return (
    <span
      className={styles.avatar}
      style={{ width: size, height: size }}
      data-framed={avatar.frame ? (frame ? 'art' : 'plain') : undefined}
      aria-hidden="true"
    >
      <span className={styles.disc}>
        <img className={styles.layer} src={bust} alt="" draggable={false} />
        {mask && (
          <span
            className={styles.tint}
            style={{ background: tint, maskImage: mask, WebkitMaskImage: mask }}
          />
        )}
      </span>
      {frame && <img className={styles.frame} src={frame} alt="" draggable={false} />}
    </span>
  );
}

/** Vector placeholder bust: hair, tinted shirt, hat and an optional frame, as one SVG. */
function AvatarSvg({
  avatar,
  tint,
  size,
}: {
  avatar: AvatarState;
  /** Shirt colour from the part's `tint` (one shared shirt sprite, style-guide §2). */
  tint: string;
  size: number;
}) {
  // Many avatars share a page (HUD, Menu, S20 options): each needs its own clip id.
  const clip = useId();
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      <circle cx="32" cy="32" r="31" fill="#fff6ea" />
      <clipPath id={clip}>
        <circle cx="32" cy="32" r="30" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <path d="M12 66C12 52 21 45 32 45s20 7 20 21Z" fill={tint} stroke="#e5cdb5" strokeWidth="1.5" />
        <path d="M27 45l5 5 5-5" fill="none" stroke="#e5cdb5" strokeWidth="1.5" />
        {avatar.hair === 'av_hair_3' && <circle cx="47" cy="40" r="6" fill={HAIR} />}
        {avatar.hair === 'av_hair_1' && (
          <path d="M17 33C14 18 22 11 32 11s18 7 15 22l-2 7-2-14H21l-2 14Z" fill={HAIR} />
        )}
        <circle cx="32" cy="31" r="13.5" fill={SKIN} />
        {avatar.hair === 'av_hair_1' && <path d="M19 27c3-9 23-11 26 0-7-3-19-3-26 0Z" fill={HAIR} />}
        {avatar.hair === 'av_hair_2' && (
          <path d="M18 30c-2-12 6-19 14-19s16 6 14 19l-4-6-3 3-3-5-4 4-3-5-4 4-3-3Z" fill={HAIR} />
        )}
        {avatar.hair === 'av_hair_3' && (
          <path d="M18 31c-2-12 5-19 14-19s16 7 14 19c-4-6-10-8-14-8s-10 2-14 8Z" fill={HAIR} />
        )}
        <circle cx="27" cy="32" r="1.9" fill={INK} />
        <circle cx="37" cy="32" r="1.9" fill={INK} />
        <ellipse cx="24" cy="36" rx="2.6" ry="1.5" fill="#f7a1b5" opacity="0.8" />
        <ellipse cx="40" cy="36" rx="2.6" ry="1.5" fill="#f7a1b5" opacity="0.8" />
        <path d="M30 37q2 1.6 4 0" fill="none" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />
        {avatar.hat === 'av_hat_bucket_pink' && (
          <>
            <path d="M20 22c0-9 24-9 24 0Z" fill="#f7b6c8" />
            <ellipse cx="32" cy="22" rx="16" ry="3.6" fill="#f39ab4" />
          </>
        )}
        {avatar.hat === 'av_hat_flower' && (
          <>
            <path d="M19 23c2-6 24-6 26 0l-1 2c-6-3-18-3-24 0Z" fill="#9fdcc4" />
            <circle cx="41" cy="20" r="3" fill="#f7a1b8" />
            <circle cx="45" cy="22" r="3" fill="#f7a1b8" />
            <circle cx="42" cy="25" r="3" fill="#f7a1b8" />
            <circle cx="42.5" cy="22.5" r="2" fill="#ffd877" />
          </>
        )}
        {avatar.hat === 'av_hat_cat_beanie' && (
          <>
            <path d="M19 25c0-11 26-11 26 0Z" fill="#cdbdf2" />
            <path d="M20 19l2-9 6 5ZM44 19l-2-9-6 5Z" fill="#cdbdf2" />
            <path d="M22 16l.8-3.5 2.4 2ZM42 16l-.8-3.5-2.4 2Z" fill="#f7a1b8" />
            <rect x="18.5" y="23" width="27" height="4" rx="2" fill={HAIR_DARK} opacity="0.25" />
          </>
        )}
      </g>
      {avatar.frame === 'afr_neighbor' ? (
        <>
          <circle cx="32" cy="32" r="30" fill="none" stroke="#9fdcc4" strokeWidth="3" />
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <ellipse
              key={a}
              cx={32 + 30 * Math.cos((a * Math.PI) / 180)}
              cy={32 + 30 * Math.sin((a * Math.PI) / 180)}
              rx="3.2"
              ry="1.8"
              fill="#6fbf9c"
              transform={`rotate(${String(a + 90)} ${String(32 + 30 * Math.cos((a * Math.PI) / 180))} ${String(32 + 30 * Math.sin((a * Math.PI) / 180))})`}
            />
          ))}
        </>
      ) : (
        <circle cx="32" cy="32" r="30.5" fill="none" stroke="#ffffff" strokeWidth="2" />
      )}
    </svg>
  );
}
